/**
 * Shift and operator attribution — who drove which machine on which shift,
 * and which of the day's alarms and abuse events happened on their watch.
 *
 * ## Why
 *
 * The data pack records each machine's day (`shift_daily`: hours, alarms,
 * abuse) and each day's alarm counts (`events_daily`) with **no shift and no
 * operator**. Routing an operator issue to the Operating Supervisor needs
 * both. This engine synthesises them — deterministically, from the rosters in
 * `seed/operators.ts` and a seeded hash — under one hard rule:
 *
 * > **Every split sums back exactly to its source.** Each `shift_daily` row's
 * > hours are split D/N in tenths of an hour; each `events_daily` count and
 * > each day's `abuse` count is split in whole events. {@link reconcile}
 * > proves it row by row, and `scripts/oem/check-reliability.mjs` fails the
 * > build if it ever stops being true.
 *
 * ## How
 *
 * 1. **Crews on shift** — {@link rosterFor}: the site's roster pattern gives
 *    the day crew and the night crew for a date.
 * 2. **Operator on a machine** — {@link operatorOn}: the crew's operator whose
 *    `homeMachine` it is, except that on a seeded 12 % of machine-shifts two
 *    crew-mates of the same class swap machines. Pinned machine-shifts
 *    (`STORY_PINS`) never swap. A pure function of machine, date and shift —
 *    it does not need `shift_daily`.
 * 3. **Hours** — each day's hours split D/N at a seeded 44–56 % day share.
 * 4. **Events** — each unit event is placed on D or N with probability equal
 *    to the shift's share of the day's hours (seeded), except where a story
 *    split overrides it (`STORY_SPLITS`). Events dated `2026-09-09` — the
 *    dataset stops at 06:00 that morning — fall in the night shift that
 *    started on 08 Sep.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness — `./hash.ts` only.
 *
 * @see docs/engines/reliability.md § Attribution
 * @see src/versions/v1/shared/seed/operators.ts
 */

import { BY_ID, FLEET, HEALTH } from '../../data/core.ts';
import type { MachineClass, MineCode } from '../../schema/machine.ts';
import type {
  CrewCode,
  ExceedanceEvent,
  OperatorRecord,
  ShiftAssignment,
  ShiftCode,
} from '../../schema/operations.ts';
import type { DailyMetricRow, EventDailyRow, ShiftDailyRow } from '../../schema/telemetry.ts';
import { OEM_TODAY } from '../oem-clock.ts';
import { ABUSE_CODE, alarmInfo } from '../seed/alarm-codes.ts';
import { metricInfoFor } from '../seed/metric-info.ts';
import {
  OPERATOR_BY_ID,
  OPERATORS,
  ROSTER_PATTERNS,
  STORY_PINS,
  STORY_SPLITS,
  operatorId as slotOperatorId,
  type StorySplit,
} from '../seed/operators.ts';
import { addDays, daysBetween, unit } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';

/** Probability that two crew-mates swap machines on a given shift. */
export const SWAP_RATE = 0.12;

/** The two shifts, day first. */
export const SHIFTS: readonly ShiftCode[] = ['D', 'N'];

/** What {@link attribute} reads. */
export interface AttributionInput {
  readonly shiftDaily: readonly ShiftDailyRow[];
  readonly eventsDaily: readonly EventDailyRow[];
  /** Optional — fills `ExceedanceEvent.value` when present. */
  readonly dailyMetric?: readonly DailyMetricRow[];
}

/** The attributed window. */
export interface Attribution {
  /** Two per `shift_daily` row: D then N. */
  readonly assignments: readonly ShiftAssignment[];
  /** One per machine × shift date × shift × code with a non-zero count. */
  readonly events: readonly ExceedanceEvent[];
  /** The roster the assignments draw from (`seed/operators.ts`). */
  readonly operators: readonly OperatorRecord[];
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

const FOUR_PANEL: readonly (readonly [CrewCode, CrewCode])[] = [
  ['A', 'B'],
  ['C', 'D'],
  ['B', 'A'],
  ['D', 'C'],
];
const FIFO_FIXED: readonly (readonly [CrewCode, CrewCode])[] = [
  ['A', 'B'],
  ['C', 'D'],
];

const mod = (n: number, m: number): number => ((n % m) + m) % m;

/** The day crew and the night crew at `mine` on `date` (shift start date). */
export function rosterFor(mine: MineCode, date: string): Record<ShiftCode, CrewCode> {
  const site = ROSTER_PATTERNS[mine];
  const days = daysBetween(site.anchor, date);
  const [d, n] =
    site.pattern === '4-panel'
      ? FOUR_PANEL[mod(Math.floor(days / 7), 4)]
      : FIFO_FIXED[mod(Math.floor(days / 14), 2)];
  return { D: d, N: n };
}

/** Machines of one class at one mine, in fleet order — the roster slots. */
function classSlots(mine: MineCode, cls: MachineClass): string[] {
  return FLEET.filter((f) => f.mine === mine && f.cls === cls).map((f) => f.machine_id);
}

const pinOf = (machine: string, shift: ShiftCode) =>
  STORY_PINS.find((p) => p.machine === machine && p.shift === shift);

/**
 * Who drove `machine` on the `shift` that started on `date`, and whether that
 * was pinned by a story override. Pure: roster + seeded swaps + pins.
 */
export function operatorOn(
  machine: string,
  date: string,
  shift: ShiftCode,
): { operatorId: string; crew: CrewCode; pinned: boolean } {
  const m = BY_ID[machine];
  const crew = rosterFor(m.mine, date)[shift];
  const slots = classSlots(m.mine, m.cls);
  // Crew-mates' home operators, then seeded pairwise swaps among unpinned slots.
  const ops = slots.map((_, i) => slotOperatorId(m.mine, m.cls, crew, i));
  const free = slots.map((id, i) => ({ id, i })).filter(({ id }) => {
    const pin = pinOf(id, shift);
    return !(pin && OPERATOR_BY_ID[pin.operatorId]?.crew === crew);
  });
  if (free.length >= 2) {
    for (let k = 0; k < free.length; k += 1) {
      if (unit(`swap|${m.mine}|${m.cls}|${date}|${shift}|${k}`) < SWAP_RATE) {
        const a = free[k].i;
        const b = free[(k + 1) % free.length].i;
        [ops[a], ops[b]] = [ops[b], ops[a]];
      }
    }
  }
  const idx = slots.indexOf(machine);
  const pin = pinOf(machine, shift);
  const pinned = !!pin && OPERATOR_BY_ID[pin.operatorId]?.crew === crew;
  return { operatorId: ops[idx], crew, pinned };
}

/**
 * The shift an `events_daily.day` belongs to. The dataset clock stops at
 * 06:00 on `OEM_TODAY`, so that day's events can only have happened on the
 * night shift that started the day before.
 */
export function shiftDateOf(day: string): { date: string; onlyShift: ShiftCode | null } {
  return day >= OEM_TODAY ? { date: addDays(day, -1), onlyShift: 'N' } : { date: day, onlyShift: null };
}

// ---------------------------------------------------------------------------
// Attribution
// ---------------------------------------------------------------------------

/** The machine's register component a code points at, or `null`. */
export function componentFor(machine: string, code: string): string | null {
  const info = alarmInfo(code, BY_ID[machine].cls);
  if (!info.component) return null;
  const re = new RegExp(info.component, 'i');
  return HEALTH.find((h) => h.machine_id === machine && re.test(h.component))?.component ?? null;
}

const splitFor = (machine: string, code: string): StorySplit | undefined =>
  STORY_SPLITS.find((s) => s.machine === machine && s.codes.includes(code));

/** Build the whole attribution. Deterministic for a given input. */
export function attribute(input: AttributionInput): Attribution {
  const assignments: ShiftAssignment[] = [];
  const byKey = new Map<string, ShiftAssignment>();

  // 1. Hours: every shift_daily row → a D and an N assignment.
  for (const row of input.shiftDaily) {
    const m = BY_ID[row.machine_id];
    if (!m) continue;
    const total10 = Math.round(row.hours * 10);
    const w = 0.44 + 0.12 * unit(`hours|${row.machine_id}|${row.date}`);
    const d10 = Math.round(total10 * w);
    const tenths: Record<ShiftCode, number> = { D: d10, N: total10 - d10 };
    for (const shift of SHIFTS) {
      const who = operatorOn(row.machine_id, row.date, shift);
      const a: ShiftAssignment = {
        machine: row.machine_id,
        mine: m.mine,
        date: row.date,
        shift,
        crew: who.crew,
        operatorId: who.operatorId,
        hours: tenths[shift] / 10,
        pinned: who.pinned,
      };
      assignments.push(a);
      byKey.set(`${row.machine_id}|${row.date}|${shift}`, a);
    }
  }

  const metricByKey = new Map<string, DailyMetricRow>();
  for (const d of input.dailyMetric ?? []) metricByKey.set(`${d.machine_id}|${d.date}`, d);

  const agg = new Map<string, ExceedanceEvent>();
  // Per story machine: events already placed per operator, for `spread`.
  const spreadTally = new Map<string, number>();

  const place = (
    machine: string,
    sourceDay: string,
    code: string,
    severity: ExceedanceEvent['severity'],
    n: number,
  ): void => {
    const m = BY_ID[machine];
    if (!m || n <= 0) return;
    const { date, onlyShift } = shiftDateOf(sourceDay);
    const dayHours = (s: ShiftCode) => byKey.get(`${machine}|${date}|${s}`)?.hours ?? 0;
    const opOf = (s: ShiftCode) =>
      byKey.get(`${machine}|${date}|${s}`)?.operatorId ?? operatorOn(machine, date, s).operatorId;
    const hD = dayHours('D');
    const hN = dayHours('N');
    const pDay = hD + hN > 0 ? hD / (hD + hN) : 0.5;
    const story = splitFor(machine, code);

    for (let u = 0; u < n; u += 1) {
      let shift: ShiftCode;
      const r = unit(`event|${machine}|${sourceDay}|${code}|${u}`);
      if (onlyShift) {
        shift = onlyShift;
      } else if (story?.mode === 'concentrate' && story.shift) {
        shift = r < (story.share ?? 0.8) ? story.shift : story.shift === 'D' ? 'N' : 'D';
      } else if (story?.mode === 'spread') {
        const tD = spreadTally.get(`${machine}|${opOf('D')}`) ?? 0;
        const tN = spreadTally.get(`${machine}|${opOf('N')}`) ?? 0;
        shift = tN < tD ? 'N' : 'D';
      } else {
        shift = r < pDay ? 'D' : 'N';
      }
      const operatorId = opOf(shift);
      if (story?.mode === 'spread') {
        const k = `${machine}|${operatorId}`;
        spreadTally.set(k, (spreadTally.get(k) ?? 0) + 1);
      }
      const key = `${machine}|${date}|${shift}|${code}|${sourceDay}`;
      const prev = agg.get(key);
      if (prev) {
        agg.set(key, { ...prev, count: prev.count + 1 });
      } else {
        const info = alarmInfo(code, m.cls);
        const dm = metricByKey.get(`${machine}|${sourceDay}`) ?? metricByKey.get(`${machine}|${date}`);
        const metric = info.metric && dm && dm.metric === info.metric ? info.metric : null;
        agg.set(key, {
          machine,
          mine: m.mine,
          date,
          sourceDay,
          shift,
          operatorId,
          code,
          severity,
          count: 1,
          component: componentFor(machine, code),
          sensitivity: info.sensitivity,
          metric,
          value: metric && dm ? dm.value : null,
          limit: metric ? metricInfoFor(metric).limit : null,
        });
      }
    }
  };

  // 2. Alarm counts, in date order so `spread` is stable.
  const events = input.eventsDaily
    .slice()
    .sort((a, b) => a.day.localeCompare(b.day) || a.machine_id.localeCompare(b.machine_id) || a.code.localeCompare(b.code));
  for (const e of events) place(e.machine_id, e.day, e.code, e.severity, e.n);

  // 3. Abuse, as the OP-ABUSE pseudo-code.
  for (const row of input.shiftDaily) place(row.machine_id, row.date, ABUSE_CODE, 'Advisory', row.abuse);

  return { assignments, events: [...agg.values()], operators: OPERATORS };
}

/** Fetch the telemetry domain and attribute it. */
export async function loadAttribution(): Promise<Attribution> {
  const inputs = await loadReliabilityInputs();
  return attribute(inputs);
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

/** Totals on one side of the reconciliation. */
export interface AttributionTotals {
  /** Operating hours, in tenths of an hour (integer — no float drift). */
  hoursTenths: number;
  abuse: number;
  /** Alarm events, all codes. */
  events: number;
  /** Alarm events by code. */
  eventsByCode: Readonly<Record<string, number>>;
}

/** Source versus attributed totals, plus every row that failed to sum back. */
export interface ReconcileReport {
  source: AttributionTotals;
  attributed: AttributionTotals;
  /** Row-level mismatches, e.g. `'T-09 2026-09-01 hours 161 ≠ 160'`. Empty when exact. */
  mismatches: readonly string[];
  ok: boolean;
}

/**
 * Prove that the attribution sums back to the source, in total and row by
 * row: each `shift_daily` row's hours and abuse, and each `events_daily`
 * row's count.
 */
export function reconcile(input: AttributionInput, att: Attribution = attribute(input)): ReconcileReport {
  const mismatches: string[] = [];

  const srcHours = new Map<string, number>();
  const srcAbuse = new Map<string, number>();
  for (const r of input.shiftDaily) {
    srcHours.set(`${r.machine_id}|${r.date}`, Math.round(r.hours * 10));
    srcAbuse.set(`${r.machine_id}|${r.date}`, r.abuse);
  }
  const attHours = new Map<string, number>();
  for (const a of att.assignments) {
    const k = `${a.machine}|${a.date}`;
    attHours.set(k, (attHours.get(k) ?? 0) + Math.round(a.hours * 10));
  }
  const srcEvents = new Map<string, number>();
  for (const e of input.eventsDaily) {
    const k = `${e.machine_id}|${e.day}|${e.code}`;
    srcEvents.set(k, (srcEvents.get(k) ?? 0) + e.n);
  }
  const attEvents = new Map<string, number>();
  const attAbuse = new Map<string, number>();
  for (const e of att.events) {
    if (e.code === ABUSE_CODE) {
      const k = `${e.machine}|${e.sourceDay}`;
      attAbuse.set(k, (attAbuse.get(k) ?? 0) + e.count);
    } else {
      const k = `${e.machine}|${e.sourceDay}|${e.code}`;
      attEvents.set(k, (attEvents.get(k) ?? 0) + e.count);
    }
  }

  const compare = (label: string, src: Map<string, number>, got: Map<string, number>) => {
    for (const [k, v] of src) {
      const g = got.get(k) ?? 0;
      if (g !== v) mismatches.push(`${k.replace(/\|/g, ' ')} ${label} ${g} ≠ ${v}`);
    }
    for (const [k, g] of got) if (!src.has(k) && g !== 0) mismatches.push(`${k.replace(/\|/g, ' ')} ${label} ${g} has no source row`);
  };
  compare('hours×10', srcHours, attHours);
  compare('abuse', new Map([...srcAbuse].filter(([, v]) => v > 0)), attAbuse);
  compare('events', srcEvents, attEvents);

  const sum = (m: Map<string, number>) => [...m.values()].reduce((a, b) => a + b, 0);
  const byCode = (m: Map<string, number>) => {
    const out: Record<string, number> = {};
    for (const [k, v] of m) {
      const code = k.split('|')[2];
      out[code] = (out[code] ?? 0) + v;
    }
    return out;
  };

  return {
    source: {
      hoursTenths: sum(srcHours),
      abuse: sum(srcAbuse),
      events: sum(srcEvents),
      eventsByCode: byCode(srcEvents),
    },
    attributed: {
      hoursTenths: sum(attHours),
      abuse: sum(attAbuse),
      events: sum(attEvents),
      eventsByCode: byCode(attEvents),
    },
    mismatches,
    ok: mismatches.length === 0,
  };
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

/** One machine-shift in a shift log. */
export interface ShiftLogEntry {
  date: string;
  shift: ShiftCode;
  crew: CrewCode;
  operatorId: string;
  operatorName: string;
  hours: number;
  /**
   * Operator-sensitive events (sensitivity ≠ `'none'`, on a component this
   * machine carries), abuse included.
   */
  sensitiveEvents: number;
  /** All attributed events, abuse included. */
  allEvents: number;
  codes: readonly { code: string; count: number }[];
  pinned: boolean;
}

/** A machine's window, shift by shift, oldest first — the "who was driving" table. */
export function shiftLog(att: Attribution, machine: string): ShiftLogEntry[] {
  const evs = att.events.filter((e) => e.machine === machine);
  return att.assignments
    .filter((a) => a.machine === machine)
    .sort((a, b) => a.date.localeCompare(b.date) || a.shift.localeCompare(b.shift))
    .map((a) => {
      const mine = evs.filter((e) => e.date === a.date && e.shift === a.shift);
      const codes = new Map<string, number>();
      for (const e of mine) codes.set(e.code, (codes.get(e.code) ?? 0) + e.count);
      return {
        date: a.date,
        shift: a.shift,
        crew: a.crew,
        operatorId: a.operatorId,
        operatorName: OPERATOR_BY_ID[a.operatorId]?.name ?? a.operatorId,
        hours: a.hours,
        sensitiveEvents: mine
          .filter((e) => e.sensitivity !== 'none' && e.component !== null)
          .reduce((s, e) => s + e.count, 0),
        allEvents: mine.reduce((s, e) => s + e.count, 0),
        codes: [...codes].map(([code, count]) => ({ code, count })).sort((x, y) => y.count - x.count),
        pinned: a.pinned,
      };
    });
}

/** One operator's hours and events on one machine. */
export interface MachineOperatorShare {
  operatorId: string;
  name: string;
  crew: CrewCode;
  hours: number;
  hoursShare: number;
  events: number;
  eventShare: number;
  synthesised: boolean;
}

/**
 * Each operator's share of a machine's hours and of its events, optionally
 * restricted to one component and/or a set of codes. Operator-sensitive
 * events on a component this machine carries only (sensitivity ≠ `'none'`,
 * component ≠ `null`). Sorted by events, most first.
 */
export function machineOperatorShares(
  att: Attribution,
  machine: string,
  filter: { component?: string; codes?: readonly string[] } = {},
): MachineOperatorShare[] {
  const hours = new Map<string, number>();
  for (const a of att.assignments) {
    if (a.machine === machine) hours.set(a.operatorId, (hours.get(a.operatorId) ?? 0) + a.hours);
  }
  const events = new Map<string, number>();
  for (const e of att.events) {
    if (e.machine !== machine || e.sensitivity === 'none' || e.component === null) continue;
    if (filter.component !== undefined && e.component !== filter.component) continue;
    if (filter.codes && !filter.codes.includes(e.code)) continue;
    events.set(e.operatorId, (events.get(e.operatorId) ?? 0) + e.count);
  }
  const H = [...hours.values()].reduce((a, b) => a + b, 0);
  const E = [...events.values()].reduce((a, b) => a + b, 0);
  const ids = new Set([...hours.keys(), ...events.keys()]);
  return [...ids]
    .map((id) => {
      const op = OPERATOR_BY_ID[id];
      const h = hours.get(id) ?? 0;
      const e = events.get(id) ?? 0;
      return {
        operatorId: id,
        name: op?.name ?? id,
        crew: op?.crew ?? 'A',
        hours: Math.round(h * 10) / 10,
        hoursShare: H ? h / H : 0,
        events: e,
        eventShare: E ? e / E : 0,
        synthesised: op?.synthesised ?? true,
      };
    })
    .sort((a, b) => b.events - a.events || b.hours - a.hours);
}
